import { useState } from 'react';
import { evaluarFactura, formatearCLP, type EstadoFactura, type TipoDocumentoVenta } from '@lexicore/shared';
import { ErrorApi } from '../../lib/api';
import { IconoAlerta, IconoCerrar, IconoCheck, IconoMas } from '../../componentes/Iconos';
import { Panel, Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import type { ProyectoDetalle } from '../../lib/consultas';
import {
  useAgregarItemCierre,
  useAlternarItemCierre,
  useCerrarProyecto,
  useEliminarItemCierre,
  useReabrirProyecto,
} from '../../lib/mutaciones';

/**
 * Cierre formal del proyecto.
 *
 * El backend se resiste a cerrar cuando quedan ítems del checklist o facturas
 * con saldo, y devuelve los motivos. Aquí se muestran tal cual: cerrar un
 * proyecto con plata pendiente lo saca de los tableros activos y esa plata no
 * la vuelve a mirar nadie. Forzarlo se puede, pero tiene que ser una decisión
 * consciente y no un clic distraído.
 */
export function PestanaCierre({ proyecto }: { proyecto: ProyectoDetalle }) {
  const [textoNuevo, setTextoNuevo] = useState('');
  const [motivosBloqueo, setMotivosBloqueo] = useState<string[] | null>(null);
  const [notaCierre, setNotaCierre] = useState('');

  const avisos = useAvisos();
  const agregar = useAgregarItemCierre();
  const alternar = useAlternarItemCierre();
  const eliminar = useEliminarItemCierre();
  const cerrar = useCerrarProyecto();
  const reabrir = useReabrirProyecto();

  const estaCerrado = proyecto.estado === 'CERRADO';
  const pendientes = proyecto.itemsCierre.filter((item) => !item.completado);

  const saldoPendiente = proyecto.facturas.reduce((suma, factura) => {
    const situacion = evaluarFactura({
      tipoDocumento: factura.tipoDocumento as TipoDocumentoVenta,
      estado: factura.estado as EstadoFactura,
      fechaEmision: factura.fechaEmision,
      fechaVencimiento: factura.fechaVencimiento,
      montoTotal: factura.montoTotal,
      pagos: factura.pagos,
    });
    return suma + situacion.saldo;
  }, 0);

  const intentarCerrar = async (forzar: boolean) => {
    try {
      await cerrar.mutateAsync({ proyectoId: proyecto.id, forzar, notaCierre: notaCierre.trim() || undefined });
      avisos.exito(forzar ? 'Proyecto cerrado' : 'Proyecto cerrado correctamente');
      setMotivosBloqueo(null);
      setNotaCierre('');
    } catch (error) {
      // El backend explica exactamente qué falta; se muestra en vez de un
      // «no se pudo» genérico.
      if (error instanceof ErrorApi) {
        const cuerpo = error.cuerpo as { motivos?: string[] } | null;
        if (cuerpo?.motivos?.length) {
          setMotivosBloqueo(cuerpo.motivos);
          return;
        }
      }
      avisos.error(error instanceof Error ? error.message : 'No se pudo cerrar el proyecto.');
    }
  };

  const agregarItem = async () => {
    const texto = textoNuevo.trim();
    if (!texto) return;

    try {
      await agregar.mutateAsync({ proyectoId: proyecto.id, texto });
      setTextoNuevo('');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo agregar el ítem.');
    }
  };

  return (
    <>
      <Panel
        titulo="Checklist de cierre"
        subtitulo="Lo que tiene que estar listo antes de dar el proyecto por terminado"
        style={{ marginBottom: 16 }}
      >
        {!estaCerrado && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
            <input
              value={textoNuevo}
              onChange={(evento) => setTextoNuevo(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter') {
                  evento.preventDefault();
                  void agregarItem();
                }
              }}
              placeholder="Ej: acta de recepción conforme firmada"
              aria-label="Nuevo ítem del checklist"
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
              }}
            />
            <button type="button" className="btn btn-secondary" onClick={() => void agregarItem()} disabled={agregar.isPending}>
              <IconoMas size={13} />
              Agregar
            </button>
          </div>
        )}

        {proyecto.itemsCierre.length === 0 ? (
          <Vacio titulo="Sin ítems de cierre" detalle="Al adjudicar se siembran los habituales de un proyecto público." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {proyecto.itemsCierre.map((item) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                <button
                  type="button"
                  onClick={() => void alternar.mutateAsync(item.id)}
                  disabled={estaCerrado}
                  aria-label={item.completado ? `Desmarcar ${item.texto}` : `Marcar ${item.texto}`}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    flexShrink: 0,
                    cursor: estaCerrado ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1.5px solid ${item.completado ? 'var(--accent-green)' : 'var(--border-medium)'}`,
                    background: item.completado ? 'var(--accent-green)' : 'transparent',
                    color: '#fff',
                  }}
                >
                  {item.completado && <IconoCheck size={12} />}
                </button>

                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: item.completado ? 'var(--text-muted)' : 'var(--text-primary)',
                    textDecoration: item.completado ? 'line-through' : undefined,
                  }}
                >
                  {item.texto}
                </span>

                {!estaCerrado && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => void eliminar.mutateAsync(item.id)}
                    style={{ color: 'var(--accent-red)' }}
                    aria-label={`Eliminar ${item.texto}`}
                  >
                    <IconoCerrar size={11} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel titulo={estaCerrado ? 'Proyecto cerrado' : 'Cerrar el proyecto'}>
        {estaCerrado ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              Cerrado{proyecto.fechaFinReal ? ` el ${proyecto.fechaFinReal.slice(0, 10)}` : ''}. Ya no aparece entre los
              proyectos activos ni genera alertas.
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  await reabrir.mutateAsync(proyecto.id);
                  avisos.exito('Proyecto reabierto');
                } catch (error) {
                  avisos.error(error instanceof Error ? error.message : 'No se pudo reabrir.');
                }
              }}
              disabled={reabrir.isPending}
            >
              Reabrir proyecto
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
              <div
                style={{
                  flex: 1,
                  minWidth: 200,
                  padding: '12px 14px',
                  background: pendientes.length > 0 ? 'var(--accent-yellow-soft)' : 'var(--accent-green-soft)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Checklist</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    marginTop: 3,
                    color: pendientes.length > 0 ? 'var(--accent-yellow)' : 'var(--accent-green)',
                  }}
                >
                  {pendientes.length > 0 ? `${pendientes.length} pendiente(s)` : 'Todo completo'}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: 200,
                  padding: '12px 14px',
                  background: saldoPendiente > 0 ? 'var(--accent-yellow-soft)' : 'var(--accent-green-soft)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Por cobrar</div>
                <div
                  className="td-mono"
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    marginTop: 3,
                    color: saldoPendiente > 0 ? 'var(--accent-yellow)' : 'var(--accent-green)',
                  }}
                >
                  {saldoPendiente > 0 ? formatearCLP(saldoPendiente) : 'Todo cobrado'}
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 18 }}>
              <label htmlFor="notaCierre">Nota de cierre</label>
              <input
                id="notaCierre"
                value={notaCierre}
                onChange={(evento) => setNotaCierre(evento.target.value)}
                placeholder="Opcional: queda registrada en la bitácora"
              />
            </div>

            {motivosBloqueo && (
              <div
                style={{
                  padding: '14px 16px',
                  background: 'var(--accent-yellow-soft)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: 18,
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                  <IconoAlerta size={15} style={{ color: 'var(--accent-yellow)', flexShrink: 0, marginTop: 1 }} />
                  <strong style={{ fontSize: 12.5, color: 'var(--accent-yellow)' }}>
                    El proyecto todavía no está listo para cerrarse
                  </strong>
                </div>
                <ul style={{ margin: 0, paddingLeft: 26, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                  {motivosBloqueo.map((motivo, indice) => (
                    <li key={indice}>{motivo}</li>
                  ))}
                </ul>
                <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMotivosBloqueo(null)}>
                    Voy a resolverlos
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => void intentarCerrar(true)}
                    disabled={cerrar.isPending}
                  >
                    Cerrar de todas formas
                  </button>
                </div>
              </div>
            )}

            {!motivosBloqueo && (
              <button type="button" className="btn btn-primary" onClick={() => void intentarCerrar(false)} disabled={cerrar.isPending}>
                {cerrar.isPending ? 'Cerrando…' : 'Cerrar proyecto'}
              </button>
            )}
          </>
        )}
      </Panel>
    </>
  );
}
